/**
 * QrScanner.js
 *
 * The phone's back camera, reading QR codes (the PNR on a guest's Wallet pass).
 * Uses the browser's own BarcodeDetector where it reads QR codes (Android Chrome), and the
 * barcode-detector library (zxing-cpp in WebAssembly) everywhere else, e.g. iPhone Safari.
 * The camera needs HTTPS (or localhost).
 */

import React, { useEffect, useRef, useState } from "react";
import { BarcodeDetector as ZXingBarcodeDetector, setZXingModuleOverrides } from "barcode-detector/ponyfill";
import { Camera, CameraOff } from "lucide-react";

// The WebAssembly comes from this site (public/zxing), not a CDN, so scanning doesn't depend on one at the door
setZXingModuleOverrides({
    locateFile: (path, prefix) => (path.endsWith(".wasm") ? "/zxing/zxing_reader-3.1.3.wasm" : prefix + path),
});

const SCAN_EVERY_MS = 200;

/**
 * The browser's own detector if it reads QR codes, otherwise the WebAssembly one
 */
const makeDetector = async () => {
    try {
        if ("BarcodeDetector" in window && (await window.BarcodeDetector.getSupportedFormats()).includes("qr_code")) {
            return new window.BarcodeDetector({ formats: ["qr_code"] });
        }
    } catch {
        // Fall through to the WebAssembly detector
    }
    return new ZXingBarcodeDetector({ formats: ["qr_code"] });
};

/**
 * @param {Function} onCode     -   Called with each QR code's text (repeatedly while it's in view)
 * @param {Boolean} paused      -   Stop reading (the camera stays on), e.g. while a result is showing
 * @param {Function} onStart    -   Called when the camera button is pressed (a user gesture, e.g. to unlock sound)
 * @param {Function} onError    -   Called with 'denied' or 'unavailable' when the camera can't be used
 * @param {Boolean} autoStart   -   Start the camera straight away
 * @returns {JSX.Element}
 */
const QrScanner = ({ onCode, paused, onStart, onError, autoStart = false }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const onCodeRef = useRef(onCode);
    const pausedRef = useRef(paused);
    // 'off' | 'starting' | 'on' | 'error'
    const [state, setState] = useState("off");
    const [error, setError] = useState("");

    onCodeRef.current = onCode;
    pausedRef.current = paused;

    const stop = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
        setState("off");
    };

    const start = async () => {
        onStart?.();
        setState("starting");
        setError("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
            streamRef.current = stream;
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
            setState("on");
        } catch (err) {
            stop();
            setState("error");
            onError?.(navigator.mediaDevices && err.name === "NotAllowedError" ? "denied" : "unavailable");
            setError(!navigator.mediaDevices
                ? "The camera needs HTTPS. Open this page over https://."
                : err.name === "NotAllowedError"
                    ? "Camera permission was denied. Allow it in the browser's settings, then try again."
                    : "Couldn't start the camera.");
        }
    };

    // Read the video a few times a second while the camera is on
    useEffect(() => {
        if (state !== "on") return undefined;
        let cancelled = false;
        let timer;
        let detector;
        const scan = async () => {
            try {
                detector = detector || await makeDetector();
                const video = videoRef.current;
                if (!pausedRef.current && video && video.readyState >= 2) {
                    const codes = await detector.detect(video);
                    if (!cancelled && codes.length > 0 && codes[0].rawValue) onCodeRef.current(codes[0].rawValue);
                }
            } catch (err) {
                console.error("QR scan failed:", err);
            }
            if (!cancelled) timer = setTimeout(scan, SCAN_EVERY_MS);
        };
        scan();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [state]);

    // Start straight away when asked (e.g. a Scan button the guest already tapped)
    useEffect(() => {
        if (autoStart) start();
        // Only when the scanner appears
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Turn the camera off when leaving the page
    useEffect(() => () => streamRef.current?.getTracks().forEach((track) => track.stop()), []);

    return (
        <div className="flex flex-col gap-3">
            <div className="relative w-full aspect-square sm:aspect-video rounded-2xl overflow-hidden" style={{ background: "#0E1726" }}>
                <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" style={{ display: state === "on" ? "block" : "none" }} />
                {state === "on" && <div className="absolute inset-[18%] rounded-xl border-4 border-white/80 pointer-events-none" aria-hidden="true" />}
                {state !== "on" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-white">
                        <Camera size={40} aria-hidden="true" />
                        <p className="text-sm opacity-80">{state === "starting" ? "Starting the camera…" : "Scan the QR code on a guest's Wallet pass"}</p>
                        {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
                    </div>
                )}
            </div>
            {state === "on" ? (
                <button type="button" onClick={stop} className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-6 py-2.5 text-sm font-bold tracking-[0.16em]" style={{ borderColor: "#1B2A41" }}>
                    <CameraOff size={16} aria-hidden="true" /> STOP CAMERA
                </button>
            ) : (
                <button type="button" onClick={start} disabled={state === "starting"} className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold tracking-[0.16em] disabled:opacity-60" style={{ background: "#1B2A41", color: "#FBF7EF" }}>
                    <Camera size={16} aria-hidden="true" /> START CAMERA
                </button>
            )}
        </div>
    );
};

export default QrScanner;
